import { useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import type { PurchaseRequestDetailQuery } from 'generated/resources/graphql'
import {
  PurchaseRequestDetailDocument,
  PurchaseRequestFragment,
  UpdatePurchaseRequestDocument,
} from 'generated/resources/graphql'

import DropZone from 'modules/core/components/DropZone'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropItem from 'modules/procurement/components/DropItem'

type ProcurementFile =
  | NonNullable<PurchaseRequestDetailQuery['procurement']['purchaseRequest']>['files'][number]
  | NonNullable<
      NonNullable<
        NonNullable<
          PurchaseRequestDetailQuery['procurement']['purchaseRequest']
        >['uploadDocumentTask']
      >[number]
    >['files'][number]

interface FileUploadModalProps {
  isOpen: boolean
  onClose: () => void
  purchaseRequest: PurchaseRequestFragment
  companyId: number
}

const FileUploadModal = ({ isOpen, onClose, purchaseRequest, companyId }: FileUploadModalProps) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const objectUrlsRef = useRef<Map<File, string>>(new Map())

  useEffect(() => {
    // Cleanup object URLs on unmount
    const urlsMap = objectUrlsRef.current
    return () => {
      urlsMap.forEach((url) => URL.revokeObjectURL(url))
      urlsMap.clear()
    }
  }, [])

  // Mutation to update purchase request with files
  const [updatePurchaseRequest, { loading: isUploading }] = useMutation(
    UpdatePurchaseRequestDocument,
    {
      extractorFn: (data) => data.procurementMutations.updateProcurementPurchaseRequest,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  const fileToProcurementFile = (file: File, index: number): ProcurementFile => {
    if (!objectUrlsRef.current.has(file)) {
      const url = URL.createObjectURL(file)
      objectUrlsRef.current.set(file, url)
    }
    return {
      __typename: 'ProcurementFile',
      id: index,
      filename: file.name,
      url: objectUrlsRef.current.get(file) || '',
      contentType: file.type || 'application/octet-stream',
      byteSize: file.size,
    }
  }

  const handleFileDrop = useUnsafeCallback((acceptedFiles: File[]) => {
    setPendingFiles((prev) => [...prev, ...acceptedFiles])
  }, [])

  const handleFileRemove = useUnsafeCallback((fileToRemove: File) => {
    const url = objectUrlsRef.current.get(fileToRemove)
    if (url) {
      URL.revokeObjectURL(url)
      objectUrlsRef.current.delete(fileToRemove)
    }
    setPendingFiles((prev) => prev.filter((file) => file !== fileToRemove))
  }, [])

  const handleFileUpload = useUnsafeCallback(async () => {
    if (pendingFiles.length === 0) return

    const result = await updatePurchaseRequest({
      variables: {
        id: purchaseRequest.id,
        companyId: companyId,
        amount: purchaseRequest.cost.cents,
        currency: purchaseRequest.cost.currency,
        files: pendingFiles,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.upload_files_modal.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.detail.upload_files_modal.success'),
      })
      // Cleanup all object URLs
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      objectUrlsRef.current.clear()
      setPendingFiles([])
      onClose()
    }
  }, [
    pendingFiles,
    purchaseRequest.id,
    purchaseRequest.cost,
    companyId,
    updatePurchaseRequest,
    onClose,
  ])

  const handleModalClose = useUnsafeCallback(() => {
    // Cleanup all object URLs
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrlsRef.current.clear()
    setPendingFiles([])
    onClose()
  }, [onClose])

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={handleModalClose}
      title={i18n.t('procurement.purchase_requests.detail.upload_files_modal.title')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.detail.upload_files_modal.confirm'),
        onClick: handleFileUpload,
        loading: isUploading,
        disabled: pendingFiles.length === 0,
      }}
    >
      <Box flexDirection="column" gap="s12">
        <DropZone
          onDrop={handleFileDrop}
          accept={{
            'application/pdf': [],
            'image/jpeg': [],
            'image/png': [],
            'image/gif': [],
          }}
          multiple
          loading={isUploading}
          placeholder={i18n.t(
            'procurement.purchase_requests.detail.upload_files_modal.placeholder'
          )}
        />

        {pendingFiles.length > 0 && (
          <Box flexDirection="column" gap="s8">
            {pendingFiles.map((file, index) => {
              const procurementFile = fileToProcurementFile(file, index)
              return (
                <DropItem
                  key={`${file.name}-${index}`}
                  file={procurementFile}
                  onRemove={() => handleFileRemove(file)}
                />
              )
            })}
          </Box>
        )}
      </Box>
    </F0Dialog>
  )
}

export default FileUploadModal
